/**
 * Cmarkdown Component
 * Main markdown parser and renderer component
 */
"use client";
import type { CSSProperties, ReactNode } from "react";
import { useMemo } from "react";
import { FontWeight } from "@/theme/typography";
import { Cmarkdownparagraph } from "../Cmarkdownparagraph/Cmarkdownparagraph";
import {
  renderMarkdownTokens,
  type MarkdownRenderOptions,
  type RenderMarkdownFenceInput,
} from "./markdown-block-renderer";
import {
  renderInlineTokens,
  SHARED_MARKDOWN_PARSER,
} from "./markdown-inline-renderer";
import { lexInlineTokens } from "./inline-lexing";
import { createMarkdownFlowStyle } from "./markdown-flow-styles";
export interface CmarkdownProps {
  content: string;
  className?: string;
  style?: CSSProperties;
  alignItems?: "flex-start" | "center" | "flex-end" | "stretch";
  /** Controls text alignment inside markdown text elements */
  textAlign?: "left" | "center" | "right";
  textSize?: number;
  textWeight?: FontWeight;
  blockquoteContentWrapperStyle?: CSSProperties;
  blockquoteContainerStyle?: CSSProperties;
  listStyle?: CSSProperties;
  listItemStyle?: CSSProperties;
  paragraphStyle?: CSSProperties;
  paragraphTextStyle?: CSSProperties;
  /**
   * Forwarded to embedded markdown visuals (e.g. Cmarkdowncode) to drop the
   * canvas border-radius and inset shadow when the markdown sits inside an
   * already-recessed parent surface.
   */
  concaved?: boolean;
  /**
   * When true, a trailing thematic break (`---` / `<hr>`) is not rendered.
   * Accordion section bodies use this so the HR does not stack on the row divider.
   */
  omitTrailingHorizontalRule?: boolean;
  /**
   * Parse as inline markdown only — no lists, headings, blockquotes, or
   * tables. Label surfaces want this: a model-authored title like
   * `1. Introduction` or `# Summary` is a NAME, and block lexing would turn
   * it into an ordered list or an H1 inside a one-line row. Emphasis, code,
   * links, and `[[…]]` references still render.
   */
  inline?: boolean;
  /**
   * Exact source bound to this render for opt-in browser-level parser evidence.
   * Omit this outside diagnostic surfaces so token evidence adds no DOM nodes.
   */
  sourceEvidence?: string;
}
export function Cmarkdown({
  content,
  className,
  style,
  alignItems = "stretch",
  textAlign,
  textSize,
  textWeight,
  blockquoteContentWrapperStyle,
  blockquoteContainerStyle,
  listStyle,
  listItemStyle,
  paragraphStyle,
  paragraphTextStyle,
  concaved = false,
  omitTrailingHorizontalRule = false,
  inline = false,
  sourceEvidence,
}: CmarkdownProps): ReactNode {
  const evidenceEnabled = sourceEvidence !== undefined;
  const renderedContent = useMemo<ReactNode>(() => {
    if (!content || content.trim() === "") {
      return null;
    }
    const renderMarkdownFence = ({
      key,
      content: fencedContent,
    }: RenderMarkdownFenceInput) => (
      <Cmarkdown
        key={key}
        content={fencedContent}
        textSize={textSize}
        textWeight={textWeight}
        textAlign={textAlign}
        blockquoteContentWrapperStyle={blockquoteContentWrapperStyle}
        blockquoteContainerStyle={blockquoteContainerStyle}
        listStyle={listStyle}
        listItemStyle={listItemStyle}
        paragraphTextStyle={paragraphTextStyle}
        concaved={concaved}
        sourceEvidence={evidenceEnabled ? fencedContent : undefined}
      />
    );
    const renderOptions: MarkdownRenderOptions = {
      textSize,
      textWeight,
      textAlign,
      blockquoteContentWrapperStyle,
      blockquoteContainerStyle,
      listStyle,
      listItemStyle,
      paragraphStyle,
      paragraphTextStyle,
      concaved,
      evidenceEnabled,
      omitTrailingHorizontalRule,
      renderMarkdownFence,
    };
    let parseFailed = false;
    let parsedContent: ReactNode = null;
    try {
      if (inline) {
        parsedContent = renderInlineTokens({
          tokens: lexInlineTokens({
            markdownParser: SHARED_MARKDOWN_PARSER,
            text: content,
          }),
          markdownParser: SHARED_MARKDOWN_PARSER,
          evidenceEnabled,
        });
      } else {
        parsedContent = renderMarkdownTokens({
          tokens: SHARED_MARKDOWN_PARSER.lexer(content),
          markdownParser: SHARED_MARKDOWN_PARSER,
          options: renderOptions,
        });
      }
    } catch {
      parseFailed = true;
    }
    if (parseFailed) {
      return (
        <Cmarkdownparagraph
          textSize={textSize}
          textWeight={textWeight}
          textAlign={textAlign}
          style={paragraphStyle}
        >
          {content}
        </Cmarkdownparagraph>
      );
    }
    // Inline mode still goes through a paragraph so the body-copy
    // typography defaults (and the caller's paragraph/text styles)
    // cascade to inline tokens exactly as they do in block mode.
    if (inline) {
      return (
        <Cmarkdownparagraph
          textSize={textSize}
          textWeight={textWeight}
          textAlign={textAlign}
          style={paragraphStyle}
          textStyle={paragraphTextStyle}
        >
          {parsedContent}
        </Cmarkdownparagraph>
      );
    }
    return parsedContent;
  }, [
    content,
    textSize,
    textWeight,
    textAlign,
    blockquoteContentWrapperStyle,
    blockquoteContainerStyle,
    listStyle,
    listItemStyle,
    paragraphStyle,
    paragraphTextStyle,
    concaved,
    omitTrailingHorizontalRule,
    inline,
    evidenceEnabled,
  ]);
  const wrapperStyle = createMarkdownFlowStyle({ alignItems, style });
  return (
    <div
      className={className}
      data-markdown-source={sourceEvidence}
      style={wrapperStyle}
    >
      {renderedContent}
    </div>
  );
}
